import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  aiApiSpec_NoteStyle,
  PusherEventPayloadMap,
} from '@timelyapp/integration-cloud-api-spec';
import { setGeneratedNotes, updateHourDraft } from 'redux/modules/hours.types';
import { showToast } from 'redux/modules/toasts.actions';
import type { IState } from 'redux/state';
import debug, { LogLevel } from 'utils/debug';
import { ticApi } from '~/redux/modules/tic/api-slice/tic.apiSlice';
import { getTicViewDemoMode } from '~redux/modules/tic/tic.demo.selectors';
import { getCurrentAccountId } from '~redux/selectors/accounts';
import { generateSyntheticNoteForHour } from './prediction.magic.actions';
import { getRequestHourNoteGenerationQuery } from './predictions.note.selectors';
import { getPredictedIntervals, getPredictionQuery } from './predictions.selectors';

/**
 * Handle suggestion note update Pusher events
 * @see useTicAiPusherManager
 */
export const handleSuggestionNoteGeneratedPusherEvent = createAsyncThunk<
  void,
  PusherEventPayloadMap['suggestion_note_update'],
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/handleSuggestionNoteGeneratedPusherEvent', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const query = getPredictionQuery(state);

  if (payload.error) {
    thunkAPI.dispatch(
      showToast({ type: 'danger', message: `Failed to generate note – ${payload.error}` })
    );
  }
  // Update the RTK Query cache
  thunkAPI.dispatch(
    ticApi.util.updateQueryData('getAiPredictionsInDateRange', query, draft => {
      // Find and update the prediction in the cache
      if (!draft?.ai_predictions) return;

      const prediction = draft.ai_predictions.find(p => p.id === payload.suggestion_id);
      if (prediction && prediction.inferred) {
        // Update the note in the inferred field
        prediction.inferred = {
          ...prediction.inferred,
          note: payload.note.with_reference || payload.note.without_reference || null,
        };
      }
    })
  );

  debug.template.ai('Updated suggestion note in cache');
});

/**
 * Handle hour note generation Pusher events
 * @see useTicAiPusherManager
 */
export const handleHourNoteGeneratedPusherEvent = createAsyncThunk<
  void,
  PusherEventPayloadMap['hour_note_generation'],
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/handleHourNoteGeneratedPusherEvent', async (payload, thunkAPI) => {
  // Debug toast + logging:
  thunkAPI.dispatch(showToast({ message: 'Note generated for hour', logLevel: LogLevel.Debug }));
  debug.template.ai('Received hour note generation', payload);

  if (payload.error) {
    thunkAPI.dispatch(
      showToast({ type: 'danger', message: `Failed to generate note – ${payload.error}` })
    );
  }

  // Update the hour draft with the generated note
  if (payload.note) {
    thunkAPI.dispatch(
      setGeneratedNotes({
        with_reference: payload.note.with_reference || '',
        without_reference: payload.note.without_reference || '',
      })
    );
    thunkAPI.dispatch(updateHourDraft({ _shouldRegenerateNote: false, _isGeneratingNote: false }));
  }
});

// Requests (proxied  to AI API) for note generation

/**
 * Fires Hour/Draft note generation (synthetically, if demo mode)
 * @see AutofillBar
 */
export const requestHourNoteGeneration = createAsyncThunk<
  void,
  void,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/requestHourNoteGeneration', async (_, { dispatch, getState }) => {
  const state = getState();
  const query = getRequestHourNoteGenerationQuery(state);
  const ticViewDemoMode = getTicViewDemoMode(state);

  // Non-demo mode handles states via RTKQ's requestHourNoteGeneration::onQueryStarted hooks
  if (!ticViewDemoMode.enabled) {
    dispatch(ticApi.endpoints.requestHourNoteGeneration.initiate({ body: query }));
  }
  // Use synthetic notes for demo mode
  else {
    dispatch(updateHourDraft({ _shouldRegenerateNote: false, _isGeneratingNote: true }));
    dispatch(generateSyntheticNoteForHour()).then(() => {
      dispatch(updateHourDraft({ _isGeneratingNote: false }));
    });
  }
});

/**
 * Fires note generation request for _all_ suggestions
 * @see MemoryTimelineLaneAiPredictionsHeader
 */
export const requestSuggestionNoteGeneration = createAsyncThunk<
  void,
  { style: aiApiSpec_NoteStyle },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/requestSuggestionNoteGeneration', async (params, { getState, dispatch }) => {
  const state = getState();
  const predictions = getPredictedIntervals(state);
  if (!predictions.length) return;

  // Dispatch the API call to generate notes for all suggestions
  // The API slice will handle logging, analytics, and toasts
  const body = {
    suggestion_ids: predictions.map(prediction => prediction.id),
    timely_account_id: getCurrentAccountId(state),
    style: params.style,
  };
  dispatch(ticApi.endpoints.requestSuggestionNoteGeneration.initiate({ body }));
});
